import { Dropdown as AntdDropdown, ConfigProvider } from 'antd';
import styles from './dropdown.module.scss';
import { colorPalette } from 'shared/constants';
import { useCallback, useLayoutEffect, useId, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { FOCUSABLE_SELECTORS } from 'src/components/common/items-navigator';

/**
 * @typedef {import('antd').DropdownProps & {
 *  items: import('antd').MenuProps['items'],
 *  onSelect: (key: string) => void,
 *  activeKey: string,
 *  showArrowIcon?: boolean,
 *  bordered?: boolean,
 *  childrenWrapperStyle?: Record<string, any>,
 * }} DropdownProps
 */

/**
 * Dropdown component
 * @param {DropdownProps} props
 *
 * @example
 * // Basic usage with bordered style
 * import { Dropdown } from './Dropdown';
 *
 * const items = [
 *   { label: 'Option 1', key: 'option1' },
 *   { label: 'Option 2', key: 'option2' },
 *   { label: 'Option 3', key: 'option3' },
 * ];
 *
 * const handleSelect = (key) => {
 *   console.log(`Selected: ${key}`);
 * };
 *
 * <Dropdown
 *   items={items}
 *   onSelect={handleSelect}
 *   activeKey="option1"
 *   bordered
 * >
 *   Select an Option
 * </Dropdown>;
 *
 * @example
 * // Dropdown with arrow icon and custom styles
 * import { Dropdown } from './Dropdown';
 *
 * const items = [
 *   { label: 'Menu Item 1', key: 'item1', style: { fontWeight: 'bold' } },
 *   { label: 'Menu Item 2', key: 'item2' },
 * ];
 *
 * <Dropdown
 *   items={items}
 *   onSelect={(key) => console.log(`Selected: ${key}`)}
 *   activeKey="item2"
 *   showArrowIcon
 * >
 *   Open Menu
 * </Dropdown>;
 *
 * @example
 * // Controlling visibility with `hide` and managing the `open` state
 *   const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
 *   const otherDialog = useRef(null);
 *
 *   const handleDropdownOpenChange = (open) => {
 *     if (!open) {
 *       otherDialog.current.closeDialog();
 *     }
 *     setSettingsDropdownOpen(open);
 *   };
 *
 *   const items = [
 *     { key: 'settings', label: <NavLink to="/settings">Settings</NavLink>, hide: false },
 *     { key: 'feedback', label: 'Give Feedback', onClick: () => console.log('Feedback') },
 *     {
 *       key: 'qr_code',
 *       label: <div ref={otherDialog}>Other Dialog</div>,
 *       style: { padding: 0 },
 *       onClick: (e) => e.domEvent.preventDefault(),
 *     },
 *   ];
 *
 *   return (
 *     <Dropdown
 *       items={items}
 *       open={settingsDropdownOpen}
 *       onOpenChange={handleDropdownOpenChange}
 *     >
 *       <div>
 *         Open Menu
 *       </div>
 *     </Dropdown>
 *   );
 */
export function Dropdown({
  items,
  onSelect,
  activeKey,
  showArrowIcon = false,
  bordered = false,
  childrenWrapperStyle = {},
  disabled = false,
  open: initOpen,
  'aria-label': ariaLabel,
  popupRender,
  ...props
}) {
  const menuTriggerRef = useRef(null);
  const [open, setOpen] = useState(initOpen);
  const [openedViaKeyboard, setOpenedViaKeyboard] = useState(false);
  const dropdownId = useId();

  const close = () => {
    menuTriggerRef?.current?.focus();
    setOpen(false);
  };

  const onTabFromMenuItem = useCallback(
    e => {
      if (e.key === 'Tab' && !e.shiftKey && open) {
        // Forward Tab: close dropdown and let focus naturally move to next element
        e.preventDefault();
        setOpen(false);

        // Find the next focusable element after the trigger
        setTimeout(() => {
          const focusableElements = document.querySelectorAll(FOCUSABLE_SELECTORS.join(', '));
          const triggerElement = menuTriggerRef.current;
          const currentIndex = Array.from(focusableElements).indexOf(triggerElement);
          const nextElement = focusableElements[currentIndex + 1];

          if (nextElement) {
            nextElement.focus();
          }
        }, 50);
      }
    },
    [open],
  );

  const onShiftTab = useCallback(
    e => {
      // Prevent default behavior of Shift + Tab to avoid moving focus outside the dropdown
      if (e.key === 'Tab' && e.shiftKey && open) {
        e.preventDefault();
        close();
      }
    },
    [open],
  );

  const onEscape = useCallback(
    e => {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        close();
      }
    },
    [open],
  );

  const focusFirstMenuItem = useCallback(() => {
    if (!openedViaKeyboard) return;

    setTimeout(() => {
      const dropdownElement = document.getElementById(dropdownId);
      const firstItem = dropdownElement?.firstChild;
      if (firstItem) {
        firstItem.focus();
      }
      setOpenedViaKeyboard(false);
    }, 50);
  }, [openedViaKeyboard, dropdownId]);

  useLayoutEffect(() => {
    focusFirstMenuItem();
  }, [open, focusFirstMenuItem]);

  const defaultItems = useMemo(
    () =>
      items
        .filter(item => !item?.hide)
        .map((item, index) => {
          // Destructure to remove `hide` prop so it doesn't get passed to DOM elements
          const { hide, ...itemProps } = item;
          return {
            ...itemProps,
            style: {
              maxWidth: '18rem',
              padding: '0.625rem 1rem',
              borderRadius: 0,
              borderBottom:
                index < items.length - 1 ? `1px solid ${colorPalette.primaryMain1}` : 'none',
              // remove the outline on focus:
              outline: 'none',
              ...itemProps.style,
            },
          };
        }),
    [items],
  );

  const defaultMenu = useMemo(
    () =>
      /**
       * @type {import('antd').MenuProps}
       */
      ({
        ...props.menu,
        items: defaultItems,
        onClick: ({ key, domEvent }) => {
          if (onSelect) onSelect(key);

          const menuItem = domEvent?.target?.closest?.('[role="menuitem"]') || domEvent?.target;
          const link = menuItem?.querySelector?.('a');

          if (link) {
            // For keyboard Enter, rc-menu clicks the <li> but the child <a>
            // is never activated. detail === 0 distinguishes programmatic
            // clicks from real mouse clicks.
            if (domEvent?.detail === 0) {
              link.click();
            }
            setOpen(false);
            return;
          }

          // Focus the trigger immediately so overlays opened by menu items
          // record it as the element to restore focus to on close
          menuTriggerRef?.current?.focus();

          // Close dropdown after selection
          setTimeout(() => {
            close();
          }, 100);
        },
        onKeyDown: e => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
          }
          onEscape(e);
          onShiftTab(e);
          onTabFromMenuItem(e);
        },
        selectable: true,
        selectedKeys: [activeKey],
        style: {
          maxHeight: '36rem',
          maxWidth: '18rem',
          overflow: 'auto',
          padding: 0,
          ...props.menu?.style,
        },
        id: dropdownId,
      }),
    [
      defaultItems,
      props.menu,
      onSelect,
      activeKey,
      onShiftTab,
      onTabFromMenuItem,
      dropdownId,
      onEscape,
    ],
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Dropdown: {
            controlItemBgHover: colorPalette.primaryMain1,
            colorText: colorPalette.primaryMain7,
          },
        },
      }}>
      <AntdDropdown
        placement="bottomLeft"
        trigger={['hover']}
        disabled={disabled}
        {...props}
        menu={defaultMenu}
        open={open}
        popupRender={popupRender}
        onOpenChange={newOpen => {
          setOpen(newOpen);
          props.onOpenChange?.(newOpen);
        }}>
        <div
          ref={menuTriggerRef}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            props.onClick?.();
          }}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : 'false'}
          aria-disabled={disabled ? 'true' : 'false'}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              setOpenedViaKeyboard(true);
              setOpen(true);
            }
            onShiftTab(e);
            onEscape(e);
          }}
          className={classNames(
            styles.childrenWrapper,
            {
              [styles.bordered]: bordered,
              [styles.disabled]: disabled,
              [styles.showArrowIcon]: showArrowIcon,
            },
            childrenWrapperStyle,
          )}>
          {props.children}
          {showArrowIcon && (
            <FontAwesomeIcon
              icon={faAngleDown}
              color={colorPalette.neutral1}
              className={styles.arrowIcon}
            />
          )}
        </div>
      </AntdDropdown>
    </ConfigProvider>
  );
}
